import ky from 'ky';

import { cmsWebClient } from '@tmh-website/services/cms.client.service';
import { isCarDetailPath, isKnowledgeCenterGroupPath, isNewsArticlePath } from '@tmh-website/services/page.service';
import { type LocalePrefix, PrefixToCurrencyMap, PrefixToLocaleMap, Project } from '@tmh-website/shared/const';

import { searchForProduct } from './magento/graphql-queries.service';
import {
  SearchResultContent,
  SearchResultMagentoDeprecated,
  SearchResultProduct,
  SearchResultProductDeprecated,
  SearchResultsMagentoTypeDeprecated,
  SearchResultTerm,
  SearchResultType,
} from './search.service';

export type ProjectWithSearch = Project.Energy | Project.Website;

const PROJECT_TO_CMS_SEARCH_ENDPOINT_MAP: Record<ProjectWithSearch, string> = {
  [Project.Energy]: 'energy/search',
  [Project.Website]: 'search',
};

/**
 * Checks if a project has search functionality.
 *
 * @param project - The project to check.
 *
 * @returns `true` if the project has search functionality, `false` otherwise.
 */
export const isProjectWithSearch = (project: Project): project is ProjectWithSearch => {
  const endpoint: string = PROJECT_TO_CMS_SEARCH_ENDPOINT_MAP[project];
  return typeof endpoint === 'string' && endpoint.length > 0;
};

export interface GetSearchResultResponse {
  [SearchResultType.ContentPage]: SearchResultContent[];
  [SearchResultType.KnowledgeCenter]: SearchResultContent[];
  [SearchResultType.News]: SearchResultContent[];
  [SearchResultType.Car]: SearchResultContent[];
  [SearchResultType.Product]: SearchResultProduct[];
}

export interface SearchResponseContent {
  path: string;
  title: string;
  score: number;
}

/**
 * Get search results from Strapi based on a query string
 */
export async function getSearchResult(
  query: string,
  locale: LocalePrefix,
  project: ProjectWithSearch,
  includeMagentoSearch = false
): Promise<GetSearchResultResponse | null> {
  if (query.trim().length < 3) {
    // Don't search for less than 3 characters
    return null;
  }

  const endpoint: string = PROJECT_TO_CMS_SEARCH_ENDPOINT_MAP[project];

  const newResults: GetSearchResultResponse = {
    [SearchResultType.ContentPage]: [],
    [SearchResultType.KnowledgeCenter]: [],
    [SearchResultType.News]: [],
    [SearchResultType.Car]: [],
    [SearchResultType.Product]: [],
  };

  const [searchResultsContent, searchResultsMagento] = await Promise.all([
    cmsWebClient
      .get(endpoint, {
        searchParams: { locale: PrefixToLocaleMap[locale], q: query },
      })
      .json<{ results: SearchResponseContent[] }>()
      .then(res => res.results),

    includeMagentoSearch ? searchForProduct(locale, query) : [],
  ]);

  searchResultsContent.forEach(page => {
    if (isKnowledgeCenterGroupPath(page.path, locale)) {
      newResults[SearchResultType.KnowledgeCenter].push({
        type: SearchResultType.KnowledgeCenter,
        path: page.path,
        title: page.title,
        score: page.score,
      });
    } else if (isNewsArticlePath(page.path, locale)) {
      newResults[SearchResultType.News].push({
        type: SearchResultType.News,
        path: page.path,
        title: page.title,
        score: page.score,
      });
    } else if (isCarDetailPath(page.path, locale)) {
      newResults[SearchResultType.Car].push({
        type: SearchResultType.Car,
        path: page.path,
        title: page.title,
        score: page.score,
      });
    } else {
      newResults[SearchResultType.ContentPage].push({
        type: SearchResultType.ContentPage,
        path: page.path,
        title: page.title,
        score: page.score,
      });
    }
  });

  newResults[SearchResultType.Product] = searchResultsMagento;

  return newResults;
}

/**
 * @deprecated This should be removed once the new search is ready
 */
export interface GetSearchResultResponseDeprecated {
  [SearchResultType.ContentPage]: SearchResultContent[];
  [SearchResultType.KnowledgeCenter]: SearchResultContent[];
  [SearchResultType.News]: SearchResultContent[];
  [SearchResultType.Car]: SearchResultContent[];
  [SearchResultType.Product]: SearchResultProductDeprecated[];
  [SearchResultType.Term]: SearchResultTerm[];
}

/**
 * Get search results from Strapi based on a query string
 * @deprecated This should be removed once the new search is ready
 */
export async function getSearchResultDeprecated(
  query: string,
  locale: LocalePrefix,
  project: ProjectWithSearch,
  includeMagentoSearch: boolean = false
): Promise<GetSearchResultResponseDeprecated | null> {
  if (query.trim().length < 3) {
    // Don't search for less than 3 characters
    return null;
  }

  const newResults: GetSearchResultResponseDeprecated = {
    [SearchResultType.ContentPage]: [],
    [SearchResultType.KnowledgeCenter]: [],
    [SearchResultType.News]: [],
    [SearchResultType.Car]: [],
    [SearchResultType.Product]: [],
    [SearchResultType.Term]: [],
  };

  const cmsSearchUrl: string = PROJECT_TO_CMS_SEARCH_ENDPOINT_MAP[project];

  let magentoSearchUrl =
    process.env.NEXT_PUBLIC_MAGENTO_SEARCH_API_URL ??
    process.env.MAGENTO_SEARCH_API_URL ??
    process.env.NX_MAGENTO_SEARCH_API_URL;

  magentoSearchUrl = magentoSearchUrl!.replace('{{locale}}', locale);

  const [searchResultsContent, searchResultsMagento] = await Promise.all([
    cmsWebClient
      .get(cmsSearchUrl, { searchParams: { locale: PrefixToLocaleMap[locale], q: query } })
      .json<{ results: SearchResponseContent[] }>()
      .then(res => res.results),

    includeMagentoSearch
      ? ky.get(magentoSearchUrl, { searchParams: { q: query, _: Date.now() } }).json<SearchResultMagentoDeprecated[]>()
      : [],
  ]);

  searchResultsContent.forEach(page => {
    if (isKnowledgeCenterGroupPath(page.path, locale)) {
      newResults[SearchResultType.KnowledgeCenter].push({
        type: SearchResultType.KnowledgeCenter,
        path: page.path,
        title: page.title,
        score: page.score,
      });
    } else if (isNewsArticlePath(page.path, locale)) {
      newResults[SearchResultType.News].push({
        type: SearchResultType.News,
        path: page.path,
        title: page.title,
        score: page.score,
      });
    } else if (isCarDetailPath(page.path, locale)) {
      newResults[SearchResultType.Car].push({
        type: SearchResultType.Car,
        path: page.path,
        title: page.title,
        score: page.score,
      });
    } else {
      newResults[SearchResultType.ContentPage].push({
        type: SearchResultType.ContentPage,
        path: page.path,
        title: page.title,
        score: page.score,
      });
    }
  });

  searchResultsMagento.forEach(result => {
    if (result.type === SearchResultsMagentoTypeDeprecated.Term) {
      newResults[SearchResultType.Term].push({
        type: SearchResultType.Term,
        title: result.title,
      });
    } else if (result.type === SearchResultsMagentoTypeDeprecated.Product) {
      // Parse the price from the HTML response
      const parsedPrice = result.price
        ?.replace(/(\r\n|\n|\r)/gm, '')
        .match(/data-price-type="finalPrice".+?class="price">([0-9]+,[0-9]{2})/);
      let price = '';
      if (parsedPrice && parsedPrice[1]) {
        price = Intl.NumberFormat(PrefixToLocaleMap[locale], {
          style: 'currency',
          currency: PrefixToCurrencyMap[locale],
        }).format(parseFloat(parsedPrice[1]));
      }

      if (!result.url) {
        return;
      }
      newResults[SearchResultType.Product].push({
        type: SearchResultType.Product,
        title: result.title,
        url: result.url,
        image: result.image,
        price: price,
      });
    }
  });

  return newResults;
}
